import type {
  EmailListItem,
  EmailTemplate,
  EmailTemplateInput,
  AuditLog,
  AutopilotStatus,
  Brand,
  BrandInput,
  Call,
  CallSummary,
  KnowledgeBaseInfo,
  ProviderAgent,
  Campaign,
  CampaignFilters,
  CampaignInput,
  ChangePasswordInput,
  DashboardStats,
  Lead,
  LeadInput,
  LeadStatus,
  PermissionKey,
  Product,
  ProductInput,
  ProviderConcurrency,
  Role,
  ScriptTemplate,
  ScriptTemplateInput,
  Session,
  SessionUser,
  Settings,
  SettingsInput,
  SourceConnectionInput,
  SourceSyncStatus,
  SourceSyncTest,
  UpdateProfileInput,
  User,
  UserCreateInput,
  UserListItem,
  UserResetPasswordInput,
  UserRole,
  UserUpdateInput,
} from '@fenix/shared';
import { api } from './client';

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export const authApi = {
  login: (email: string, password: string) =>
    api<{ accessToken: string; user: SessionUser }>('/api/auth/login', {
      method: 'POST',
      body: { email, password },
    }),
  logout: () => api<void>('/api/auth/logout', { method: 'POST' }),
  me: () => api<SessionUser>('/api/auth/me'),
  updateProfile: (body: UpdateProfileInput) =>
    api<SessionUser>('/api/auth/me', { method: 'PATCH', body }),
  changePassword: (body: ChangePasswordInput) =>
    api<SessionUser>('/api/auth/change-password', { method: 'POST', body }),
  sessions: () => api<Session[]>('/api/auth/sessions'),
  revokeSession: (id: string) => api<void>(`/api/auth/sessions/${id}`, { method: 'DELETE' }),
  revokeOtherSessions: () => api<{ revoked: number }>('/api/auth/sessions', { method: 'DELETE' }),
};

export const usersApi = {
  list: (search?: string) => api<UserListItem[]>('/api/users', { query: { search } }),
  get: (id: string) => api<{ user: User; sessions: Session[] }>(`/api/users/${id}`),
  create: (body: UserCreateInput) => api<User>('/api/users', { method: 'POST', body }),
  update: (id: string, body: UserUpdateInput) =>
    api<User>(`/api/users/${id}`, { method: 'PATCH', body }),
  resetPassword: (id: string, body: UserResetPasswordInput) =>
    api<User>(`/api/users/${id}/reset-password`, { method: 'POST', body }),
  unlock: (id: string) => api<User>(`/api/users/${id}/unlock`, { method: 'POST' }),
  revokeSessions: (id: string) =>
    api<{ revoked: number }>(`/api/users/${id}/sessions`, { method: 'DELETE' }),
  remove: (id: string) => api<void>(`/api/users/${id}`, { method: 'DELETE' }),
};

export interface PermissionInfo {
  key: PermissionKey;
  feature: string;
  description: string;
}
export const rolesApi = {
  list: () => api<Role[]>('/api/roles'),
  permissions: () => api<PermissionInfo[]>('/api/roles/permissions'),
  update: (role: UserRole, permissions: PermissionKey[]) =>
    api<Role>(`/api/roles/${role}/permissions`, { method: 'PUT', body: { permissions } }),
};

export const devtoolsApi = {
  sampleDataStatus: () =>
    api<{ leads: number; fixtureRows: number; scripts: number; defaultScripts: number }>(
      '/api/devtools/sample-data',
    ),
  loadSampleData: () =>
    api<{
      scriptsCreated: number;
      scriptsUpdated: number;
      productsCreated: number;
      created: number;
      updated: number;
      skipped: number;
      errors: number;
    }>('/api/devtools/sample-data', { method: 'POST' }),
  removeSampleData: () => api<{ leads: number }>('/api/devtools/sample-data', { method: 'DELETE' }),
};

export const auditApi = {
  list: (query: Record<string, string | number | undefined>) =>
    api<Paginated<AuditLog>>('/api/audit', { query }),
};

export const leadsApi = {
  list: (query: Record<string, string | number | undefined>) =>
    api<Paginated<Lead>>('/api/leads', { query }),
  facets: () =>
    api<{ sources: Facet[]; countries: Facet[]; products: Facet[] }>('/api/leads/facets'),
  get: (id: string) => api<Lead & { calls: Call[] }>(`/api/leads/${id}`),
  create: (body: LeadInput) => api<Lead>('/api/leads', { method: 'POST', body }),
  update: (id: string, body: Partial<LeadInput>) =>
    api<Lead>(`/api/leads/${id}`, { method: 'PUT', body }),
  setStatus: (id: string, status: LeadStatus) =>
    api<Lead>(`/api/leads/${id}/status`, { method: 'PATCH', body: { status } }),
  remove: (id: string) => api<void>(`/api/leads/${id}`, { method: 'DELETE' }),
  call: (
    id: string,
    body: { scriptTemplateId?: string; language?: string; useMock?: boolean } = {},
  ) => api<Call>(`/api/leads/${id}/call`, { method: 'POST', body }),
  webCall: (
    id: string,
    body: { scriptTemplateId?: string; language?: string; useMock?: boolean } = {},
  ) =>
    api<{ call: Call; accessToken: string | null }>(`/api/leads/${id}/web-call`, {
      method: 'POST',
      body,
    }),
  import: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api<ImportResult>('/api/leads/import', { method: 'POST', body: fd });
  },
};
export interface Facet {
  value: string;
  count: number;
}
export interface ImportResult {
  totalRows: number;
  parsed: number;
  created: number;
  updated: number;
  skipped: number;
  errors: { row?: number; index?: number; message: string }[];
}

export const autopilotApi = {
  status: () => api<AutopilotStatus>('/api/autopilot'),
  run: () =>
    api<{ enrolled: number; eligible: number; status: AutopilotStatus }>('/api/autopilot/run', {
      method: 'POST',
    }),
  webCall: (campaignLeadId: string) =>
    api<{ call: Call; accessToken: string | null }>(`/api/autopilot/web-call/${campaignLeadId}`, {
      method: 'POST',
    }),
};

export const brandsApi = {
  list: () => api<(Brand & { productCount: number; leadCount: number })[]>('/api/brands'),
  update: (slug: string, body: BrandInput) =>
    api<Brand>(`/api/brands/${slug}`, { method: 'PUT', body }),
  knowledgeBases: () =>
    api<{ provider: string; items: KnowledgeBaseInfo[]; error: string | null }>(
      '/api/brands/knowledge-bases',
    ),
  createKnowledgeBase: (slug: string) =>
    api<Brand>(`/api/brands/${slug}/knowledge-base`, { method: 'POST' }),
};

export const productsApi = {
  list: (includeInactive = false) =>
    api<(Product & { leadCount: number })[]>('/api/products', { query: { includeInactive } }),
  create: (body: ProductInput) => api<Product>('/api/products', { method: 'POST', body }),
  update: (id: string, body: Partial<ProductInput>) =>
    api<Product>(`/api/products/${id}`, { method: 'PUT', body }),
  remove: (id: string) => api<void>(`/api/products/${id}`, { method: 'DELETE' }),
  bulkActive: (ids: string[], active: boolean) =>
    api<{ updated: number }>('/api/products/bulk-active', {
      method: 'POST',
      body: { ids, active },
    }),
};

export const scriptsApi = {
  list: (language?: string) => api<ScriptTemplate[]>('/api/scripts', { query: { language } }),
  variables: () => api<string[]>('/api/scripts/variables'),
  create: (body: ScriptTemplateInput) =>
    api<ScriptTemplate>('/api/scripts', { method: 'POST', body }),
  update: (id: string, body: Partial<ScriptTemplateInput>) =>
    api<ScriptTemplate>(`/api/scripts/${id}`, { method: 'PUT', body }),
  remove: (id: string) => api<void>(`/api/scripts/${id}`, { method: 'DELETE' }),
  preview: (id: string, leadId?: string) =>
    api<{
      systemPrompt: string;
      firstMessage: string;
      variables: Record<string, string>;
      missing: string[];
    }>(`/api/scripts/${id}/preview`, {
      method: 'POST',
      body: { leadId },
    }),
};

export const settingsApi = {
  get: () => api<Settings>('/api/settings'),
  update: (body: SettingsInput) => api<Settings>('/api/settings', { method: 'PUT', body }),
  clearKey: (which: 'retell' | 'openai' | 'twilio' | 'source' | 'resend' | 'resendWebhook') =>
    api<Settings>(`/api/settings/keys/${which}`, { method: 'DELETE' }),
  emailTemplates: () =>
    api<{ variables: string[]; templates: EmailTemplate[]; htmlStarters: Record<string, string> }>(
      '/api/settings/email-templates',
    ),
  saveEmailTemplate: (language: string, body: EmailTemplateInput) =>
    api<EmailTemplate>(`/api/settings/email-templates/${language}`, { method: 'PUT', body }),
  resetEmailTemplate: (language: string) =>
    api<EmailTemplate>(`/api/settings/email-templates/${language}`, { method: 'DELETE' }),
  emailTest: (to: string) =>
    api<{ ok: boolean; message: string }>('/api/settings/email/test', {
      method: 'POST',
      body: { to },
    }),
  // Integração com o banco de origem (buygoods_bridge)
  sourceSync: () => api<SourceSyncStatus>('/api/settings/source-sync'),
  sourceSyncTest: (body: SourceConnectionInput) =>
    api<SourceSyncTest>('/api/settings/source-sync/test', { method: 'POST', body }),
  sourceSyncRun: () => api<SourceSyncStatus>('/api/settings/source-sync/run', { method: 'POST' }),
  sourceSyncReset: () =>
    api<SourceSyncStatus>('/api/settings/source-sync/reset', { method: 'POST' }),
  languages: () => api<{ code: string; label: string }[]>('/api/settings/languages'),
  concurrency: () => api<ProviderConcurrency>('/api/settings/concurrency'),
  agents: () => api<ProviderAgent[]>('/api/settings/agents'),
  testConnection: () =>
    api<{
      ok: boolean;
      provider: string;
      agents: number;
      webhookUrl: string;
      phoneNumbers: { number: string; nickname: string | null }[];
      message: string;
    }>('/api/settings/test-connection', { method: 'POST' }),
  voices: (language?: string) =>
    api<
      {
        id: string;
        name: string;
        provider: string;
        gender: string | null;
        accent: string | null;
        previewUrl: string | null;
      }[]
    >('/api/settings/voices', {
      query: { language },
    }),
};

export const campaignsApi = {
  list: () => api<Campaign[]>('/api/campaigns'),
  get: (id: string) =>
    api<
      Campaign & {
        scriptTemplate: { id: string; name: string; language: string };
        leadStatus: Record<string, number>;
        outcomes: Record<string, number>;
      }
    >(`/api/campaigns/${id}`),
  leads: (id: string, page = 1, pageSize = 50) =>
    api<Paginated<CampaignLeadRow>>(`/api/campaigns/${id}/leads`, { query: { page, pageSize } }),
  preview: (filters: CampaignFilters) =>
    api<{
      count: number;
      sample: {
        id: string;
        name: string | null;
        phoneE164: string;
        productCodename: string | null;
      }[];
    }>('/api/campaigns/preview', {
      method: 'POST',
      body: filters,
    }),
  create: (body: CampaignInput) => api<Campaign>('/api/campaigns', { method: 'POST', body }),
  start: (id: string) => api<Campaign>(`/api/campaigns/${id}/start`, { method: 'POST' }),
  pause: (id: string) => api<Campaign>(`/api/campaigns/${id}/pause`, { method: 'POST' }),
  remove: (id: string) => api<void>(`/api/campaigns/${id}`, { method: 'DELETE' }),
};
export interface CampaignLeadRow {
  id: string;
  status: string;
  attempts: number;
  nextAttemptAt: string | null;
  lastOutcome: string | null;
  lastCallId: string | null;
  lead: {
    id: string;
    name: string | null;
    phoneE164: string;
    productCodename: string | null;
    status: string;
  };
}

export interface SmsMessage {
  id: string;
  toE164: string;
  body: string;
  checkoutUrl: string | null;
  status: string;
  errorCode: string | null;
  errorMessage: string | null;
  sentAt: string;
  deliveredAt: string | null;
}

export interface EmailMessage {
  id: string;
  toEmail: string;
  subject: string;
  reason: string;
  checkoutUrl: string | null;
  status: string;
  errorMessage: string | null;
  sentAt: string;
  deliveredAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
}

type CallDetail = Call & {
  transcriptJson: unknown;
  analysis: Record<string, unknown> | null;
  dynamicVars: Record<string, unknown> | null;
  smsMessages: SmsMessage[];
  emailMessages: EmailMessage[];
};

export const callsApi = {
  list: (query: Record<string, string | number | undefined>) =>
    api<Paginated<Call>>('/api/calls', { query }),
  get: (id: string) => api<CallDetail>(`/api/calls/${id}`),
  sync: (id: string) => api<CallDetail>(`/api/calls/${id}/sync`, { method: 'POST' }),
  email: (id: string) => api<CallDetail>(`/api/calls/${id}/email`, { method: 'POST' }),
  summary: (query: Record<string, string | number | undefined>) =>
    api<CallSummary>('/api/calls/summary', { query }),
};

export const emailsApi = {
  list: (query: Record<string, string | number | undefined>) =>
    api<Paginated<EmailListItem>>('/api/emails', { query }),
};

export const dashboardApi = {
  get: (days = 14) => api<DashboardStats>('/api/dashboard', { query: { days } }),
};

export const webhooksApi = {
  events: (limit = 50) =>
    api<
      {
        id: string;
        provider: string;
        eventType: string;
        providerCallId: string | null;
        callId: string | null;
        signatureOk: boolean;
        processedAt: string | null;
        error: string | null;
        receivedAt: string;
      }[]
    >('/api/webhooks/events', { query: { limit } }),
  mock: (payload: unknown) =>
    api<{ ok: boolean }>('/api/webhooks/mock', { method: 'POST', body: payload }),
};
